// work-log 에이전트 로컬 컴포넌트 라이브 샘플 — 실제 컴포넌트를 딥 경로로 import 해 목 데이터로 렌더한다.
// 갤러리는 apiClient 요청을 차단하므로 조회형 컴포넌트는 실패/빈 상태로 보인다(의도).
import { useState, type ReactNode } from 'react';
import { Button, IconWarningCircle, StatusHint } from '@indipia/ui-core';
import { ThumbImg } from '@indipia/agents-work-log/src/components/ThumbImg';
import { PhotoLightbox } from '@indipia/agents-work-log/src/components/PhotoLightbox';
import { Toast } from '@indipia/agents-work-log/src/components/Toast';
import { StatusChip } from '@indipia/agents-work-log/src/components/StatusChip';
import { IndiPhotoPickerModal } from '@indipia/agents-work-log/src/components/IndiPhotoPickerModal';
import {
  PhotoAttach,
  PhotoPreview,
  type LocalPhoto,
} from '@indipia/agents-work-log/src/components/OrderAttachments';
import {
  CustomerReportView,
  DEFAULT_SETTINGS,
  type ReportGroup,
} from '@indipia/agents-work-log/src/components/CustomerReportView';
import {
  WorkOrderNoticeView,
  type WorkOrderNoticeViewData,
} from '@indipia/agents-work-log/src/components/WorkOrderNoticeView';
import { PartnerAssignModal } from '@indipia/agents-work-log/src/components/PartnerAssignModal';
import { WorkOrderTabs } from '@indipia/agents-work-log/src/components/WorkOrderTabs';
import { GroupWeather } from '@indipia/agents-work-log/src/components/GroupWeather';
import { RouteMapChart } from '@indipia/agents-work-log/src/components/process-chart/RouteMapChart';
import { BarChart } from '@indipia/agents-work-log/src/components/process-chart/BarChart';
import type { ProcessStep } from '@indipia/agents-work-log/src/components/process-chart/types';
import type { BasicInfoWorklog, WorkOrderWeather } from '@indipia/agents-work-log/src/types/worklog.types';
import { DEMO_TILES } from '../../demoTiles';

// ── 목 데이터 ──────────────────────────────────────────────
const STEPS: ProcessStep[] = [
  { name: '철거', status: 'done', date: '9.28~9.30', days: '3일' },
  { name: '설비', status: 'done', date: '10.1~10.4', days: '4일' },
  { name: '전기', status: 'active', date: '10.5~10.9', days: '5일' },
  { name: '목공', status: 'pending', date: '10.10~10.15', days: '6일' },
  { name: '도배', status: 'pending', date: '10.16~10.18', days: '3일' },
  { name: '완공', status: 'finish', date: '10.20' },
];

const LOCAL_PHOTOS: LocalPhoto[] = DEMO_TILES.slice(0, 3).map((t) => ({
  id: `demo-${t.id}`,
  url: t.url,
  name: `현장사진_${t.id}.jpg`,
  source: 'FILE',
}));

const WORKLOG: BasicInfoWorklog = {
  worklogId: 101,
  siteId: 11,
  scheduleId: 7,
  siteName: '반포 래미안 32평 리모델링',
  siteAddress: '서울 서초구 반포대로 201',
  customerId: 3,
  customerName: '김민지',
  customerMobile: null,
  siteLat: null,
  siteLng: null,
  progressStatus: 'IN_PROGRESS',
  constStartDate: '2026-09-28',
  constEndDate: '2026-10-20',
};

const GROUPS: ReportGroup[] = [
  {
    date: '2026.10.05 (월)',
    dateKey: '2026-10-05',
    contents: ['전기 배선 교체를 시작했습니다.', '거실 매입등 위치를 확정했습니다.'],
    photos: DEMO_TILES.slice(0, 4).map((t) => ({ url: t.url, thumbUrl: t.url })),
  },
  {
    date: '2026.10.02 (금)',
    dateKey: '2026-10-02',
    contents: ['욕실 배관 교체와 방수 1차 작업을 마쳤습니다.'],
    photos: DEMO_TILES.slice(4, 7).map((t) => ({ url: t.url, thumbUrl: t.url })),
  },
];

const NOTICE: WorkOrderNoticeViewData = {
  senderCompanyName: '인디인테리어',
  senderRepName: '박정우',
  requestedByLabel: '현장관리자 이수진',
  recipientName: '최반장',
  requestDate: '2026-10-19',
  siteName: '반포 래미안 32평 리모델링',
  customerName: '김민지',
  orderContent: '거실 천장 몰딩 시공 부탁드립니다.\n자재는 오전 중 현장 반입 예정입니다.',
};

const WEATHERS: WorkOrderWeather[] = [
  { weatherName: '맑음', weatherIcon: '☀️', tempNow: 18, tempHigh: 22, tempLow: 11, capturedAt: '2026-10-16T08:00:00+09:00' },
  { weatherName: '비', weatherIcon: '🌧️', tempNow: 14, tempHigh: 16, tempLow: 12, capturedAt: '2026-10-15T08:00:00+09:00' },
];

// ── 상태 데모 ──────────────────────────────────────────────
function PhotoLightboxDemo() {
  const [index, setIndex] = useState<number | null>(null);
  return (
    <>
      <Button size="sm" onClick={() => setIndex(0)}>열기</Button>
      <PhotoLightbox photos={DEMO_TILES} index={index} onClose={() => setIndex(null)} onIndexChange={setIndex} />
    </>
  );
}

function ToastDemo() {
  const [toast, setToast] = useState<{ message: string; variant: 'warning' | 'info' } | null>(null);
  return (
    <div className="flex gap-2">
      <Button size="sm" onClick={() => setToast({ message: '사진은 최대 10장까지 첨부할 수 있습니다.', variant: 'warning' })}>warning</Button>
      <Button size="sm" onClick={() => setToast({ message: '작업요청을 임시저장했습니다.', variant: 'info' })}>info</Button>
      <Toast message={toast?.message ?? null} variant={toast?.variant} onClose={() => setToast(null)} />
    </div>
  );
}

function IndiPhotoPickerDemo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>열기</Button>
      <IndiPhotoPickerModal
        open={open}
        siteId={null}
        siteName="반포 래미안 32평 리모델링"
        subscribed={false}
        room={7}
        onClose={() => setOpen(false)}
        onConfirm={() => setOpen(false)}
      />
    </>
  );
}

function PhotoAttachDemo() {
  const [photos, setPhotos] = useState<LocalPhoto[]>(LOCAL_PHOTOS);
  return (
    <PhotoAttach
      photos={photos}
      onAdd={(files) =>
        setPhotos((prev) => [
          ...prev,
          ...files.map((f, i) => ({ id: `${f.name}-${Date.now()}-${i}`, url: URL.createObjectURL(f), name: f.name, source: 'FILE' as const, file: f })),
        ])
      }
      onRemove={(id) => setPhotos((prev) => prev.filter((p) => p.id !== id))}
      onOpen={() => {}}
      onOpenIndiPhoto={() => {}}
    />
  );
}

function PartnerAssignDemo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>열기</Button>
      {open && <PartnerAssignModal taskLabel="목공 · 거실 천장 몰딩" onClose={() => setOpen(false)} onAssign={() => setOpen(false)} />}
    </>
  );
}

// ── 데모 맵 ────────────────────────────────────────────────
export const demos: Record<string, () => ReactNode> = {
  // 화면 내부 헬퍼(WorkOrderCreatePage, export 안 됨) — 문구 계산 결과를 화면과 같은 StatusHint(warning)로 표시.
  // 실제 화면 아이콘은 인라인 svg 이고, 여기서는 교체 제안안인 ui-core IconWarningCircle 을 쓴다.
  foremanChangeWarning: () => (
    <div className="flex flex-col gap-2">
      <StatusHint tone="warning" role="status" icon={<IconWarningCircle />}>
        시공반장이 김도배에서 이타일로 변경됩니다.
      </StatusHint>
      <p className="text-caption text-text-secondary">작업자 선택 시 기존 반장과 다를 때만 표시 · 헬퍼는 export 되지 않아 표시 결과만 재현</p>
    </div>
  ),
  ThumbImg: () => (
    <div className="space-y-2">
      <ThumbImg url="https://example.invalid/thumb/1.jpg" className="h-24 w-32 overflow-hidden rounded-md" alt="현장 썸네일" />
      <p className="text-caption text-text-muted">API 차단 상태 → fallback</p>
    </div>
  ),
  PhotoLightbox: () => <PhotoLightboxDemo />,
  Toast: () => <ToastDemo />,
  StatusChip: () => (
    <div className="flex flex-wrap gap-2">
      {['READY', 'IN_PROGRESS', 'DONE', 'DELAYED', 'CANCELED'].map((c) => (
        <StatusChip key={c} code={c} />
      ))}
    </div>
  ),
  IndiPhotoPickerModal: () => <IndiPhotoPickerDemo />,
  PhotoAttach: () => <PhotoAttachDemo />,
  CustomerReportView: () => (
    <div className="max-h-96 overflow-auto">
      <CustomerReportView
        worklog={WORKLOG}
        settings={DEFAULT_SETTINGS}
        groups={GROUPS}
        constDays={20}
        progressRate={45}
        steps={STEPS}
        companyName="인디인테리어"
      />
    </div>
  ),
  WorkOrderNoticeView: () => (
    <div data-group="02" className="max-h-96 overflow-auto">
      <WorkOrderNoticeView
        data={NOTICE}
        photos={DEMO_TILES.slice(0, 3)}
        renderThumb={(p) => <img src={p.url} alt={p.alt} className="h-full w-full object-cover" />}
      />
    </div>
  ),
  PartnerAssignModal: () => <PartnerAssignDemo />,
  WorkOrderTabs: () => <WorkOrderTabs />,
  GroupWeather: () => (
    <div className="flex gap-4">
      {WEATHERS.map((w) => (
        <GroupWeather key={w.capturedAt} weather={w} />
      ))}
    </div>
  ),
  PhotoPreview: () => (
    <div className="flex gap-2">
      {LOCAL_PHOTOS.map((p) => (
        <PhotoPreview key={p.id} photo={p} className="h-16 w-16 rounded-md object-cover" />
      ))}
    </div>
  ),
  RouteMapChart: () => (
    <div className="w-full">
      <RouteMapChart steps={STEPS} />
    </div>
  ),
  BarChart: () => (
    <div className="w-full">
      <BarChart steps={STEPS} />
    </div>
  ),
};
